/**
 * WASSER E-commerce Platform - Mobile Bottom Navigation
 *
 * Нижняя навигация для мобильных устройств.
 * Отображается только на экранах < 768px.
 *
 * @example
 * // В layout wrapper
 * import MobileBottomNav from '../components/layout/MobileBottomNav';
 *
 * function Layout({ children }) {
 *   return (
 *     <>
 *       {children}
 *       <MobileBottomNav />
 *     </>
 *   );
 * }
 */
import CategoryIcon from '@mui/icons-material/Category';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Context imports
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';

/**
 * Маппинг путей на значения навигации
 */
const getValueFromPath = (pathname) => {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/products')) return 'catalog';
  if (pathname === '/cart' || pathname.startsWith('/checkout')) return 'cart';
  if (pathname === '/favorites') return 'favorites';
  if (
    pathname.startsWith('/profile') ||
    pathname === '/login' ||
    pathname === '/register'
  )
    return 'profile';
  return null;
};

/**
 * MobileBottomNav Component
 */
const MobileBottomNav = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { isAuthenticated } = useAuth();
  const { itemCount: cartCount } = useCart();
  const { favorites } = useFavorites();

  // Используем useMemo для вычисления текущего значения
  const currentValue = useMemo(
    () => getValueFromPath(location.pathname),
    [location.pathname]
  );

  const [selectedValue, setSelectedValue] = useState(currentValue);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Синхронизируем selectedValue с текущим путём через microtask
  useEffect(() => {
    if (currentValue !== selectedValue) {
      queueMicrotask(() => {
        setSelectedValue(currentValue);
      });
    }
  }, [currentValue, selectedValue]);

  // Скрываем при скролле вниз, показываем при скролле вверх
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    if (isMobile) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY, isMobile]);

  // Не показываем на десктопе
  if (!isMobile) {
    return null;
  }

  const handleChange = (event, newValue) => {
    setSelectedValue(newValue);

    switch (newValue) {
      case 'home':
        navigate('/');
        break;
      case 'catalog':
        navigate('/products');
        break;
      case 'cart':
        navigate('/cart');
        break;
      case 'favorites':
        navigate('/favorites');
        break;
      case 'profile':
        navigate(isAuthenticated ? '/profile' : '/login');
        break;
      default:
        break;
    }
  };

  const favoritesCount = favorites?.length || 0;

  return (
    <>
      {/* Spacer для контента, чтобы не перекрывался навигацией */}
      <Box sx={{ height: 56 }} />

      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={selectedValue}
          onChange={handleChange}
          showLabels
          sx={{
            height: 56,
            borderRadius: '16px 16px 0 0',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 0',
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              '&.Mui-selected': {
                fontSize: '0.75rem',
              },
            },
          }}
        >
          <BottomNavigationAction
            label="Главная"
            value="home"
            icon={<HomeIcon />}
          />
          <BottomNavigationAction
            label="Каталог"
            value="catalog"
            icon={<CategoryIcon />}
          />
          <BottomNavigationAction
            label="Корзина"
            value="cart"
            icon={
              <Badge
                badgeContent={cartCount > 0 ? cartCount : null}
                color="secondary"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                  },
                }}
              >
                <ShoppingCartIcon />
              </Badge>
            }
          />
          <BottomNavigationAction
            label="Избранное"
            value="favorites"
            icon={
              <Badge
                badgeContent={favoritesCount > 0 ? favoritesCount : null}
                color="secondary"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                  },
                }}
              >
                <FavoriteIcon />
              </Badge>
            }
          />
          <BottomNavigationAction
            label={isAuthenticated ? 'Профиль' : 'Войти'}
            value="profile"
            icon={<PersonIcon />}
          />
        </BottomNavigation>
      </Paper>
    </>
  );
};

export default MobileBottomNav;
